import { FormControl, Validators } from '@angular/forms';
import { effect, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { AppForm } from '../../common/forms/app.form';
import {
  FeeBaseAllBaseReadFeeCategoryAllFeeCategoryRead,
  FeeCategoryBaseAllBaseReadFeeCategoryAllFeeCategoryRead,
  FeeCategoryService, PaymentBaseAllBaseWritePaymentAllPaymentWrite
} from '../../openapi';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserDetails } from '../../store/reducers/user-details.reducer';
import { TokenData } from '../../services/ssession.service';
import { environment } from '../../../environments/environment';
import { PaymentCommonService } from '../../services/payment.common.service';
import { Observable } from 'rxjs';
import { toNumber } from '../../services/helpers';

export interface EntityType {
  key: string;
  value: string;
}

@Injectable()
export class PaymentForm extends AppForm {

  public paymentCategories$: Observable<Array<FeeCategoryBaseAllBaseReadFeeCategoryAllFeeCategoryRead>> = this.paymentCategoryService.apiFeeCategoriesGetCollectionFeeCategoryCollection({ pagination: false });

  public paymentCategories: Signal<Array<FeeCategoryBaseAllBaseReadFeeCategoryAllFeeCategoryRead> | undefined>
    = toSignal(this.paymentCategories$);

  public paymentPositions: WritableSignal<FeeBaseAllBaseReadFeeCategoryAllFeeCategoryRead[]> = signal<FeeBaseAllBaseReadFeeCategoryAllFeeCategoryRead[]>([]);

  public hasDescription: WritableSignal<boolean> = signal(false);

  entityTypes: EntityType[] = [
    { key: '1', value: 'Osoba fizyczna' },
    { key: '2', value: 'Instytucja' }
  ];

  public selectedAmount: number = 0;

  public count: number = 1;

  public multiple: WritableSignal<boolean> = signal(false);

  constructor(
    private paymentCategoryService: FeeCategoryService
  ) {
    super({
      paymentCategory: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true
      }),
      paymentPosition: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true
      }),
      entityType: new FormControl('1', {
        validators: [Validators.required],
        nonNullable: true
      }),
      clientFirstName: new FormControl({ value: '', disabled: true }, {
        validators: [Validators.required],
        nonNullable: true
      }),
      clientLastName: new FormControl({ value: '', disabled: true }, {
        validators: [Validators.required],
        nonNullable: true
      }),
      clientName: new FormControl({ value: '', disabled: true }, {
        validators: [Validators.required],
        nonNullable: true
      }),
      nip: new FormControl({ value: '', disabled: true }, {
        validators: [Validators.required],
        nonNullable: true
      }),
      clientEmail: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true
      }),
      street: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true
      }),
      streetNumber: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true
      }),
      town: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true
      }),
      totalAmount: new FormControl(0, {
        validators: [Validators.required],
        nonNullable: true
      }),
      clientDescription: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(50)],
        nonNullable: true
      })
    });
    this.entityTypeChange('1');
    effect(() => {
      const v: boolean = this.hasDescription();
      if (v) {
        this.clientDescription.enable();
      } else {
        this.clientDescription.disable();
      }
    });
  }

  private get clientDescription(): FormControl {
    return this.get('clientDescription') as FormControl;
  }

  private get paymentCategory(): FormControl {
    return this.get('paymentCategory') as FormControl;
  }

  private get paymentPosition(): FormControl {
    return this.get('paymentPosition') as FormControl;
  }

  private get clientFirstName(): FormControl {
    return this.get('clientFirstName') as FormControl;
  }

  private get clientLastName(): FormControl {
    return this.get('clientLastName') as FormControl;
  }

  private get clientName(): FormControl {
    return this.get('clientName') as FormControl;
  }

  private get nip(): FormControl {
    return this.get('nip') as FormControl;
  }

  public get totalAmount(): FormControl {
    return this.get('totalAmount') as FormControl;
  }

  public updateTotalAmount(): void {
    this.totalAmount.setValue(this.selectedAmount * this.count * 100);
  }

  public paymentPositionChange(v: string): void {
    const items = [...this.paymentPositions()];
    this.hasDescription.set(items.filter(j => j.id === v)[0]?.hasDescription || false);
    this.selectedAmount = items.filter(j => j.id === v)[0]?.price || 0;
    this.updateTotalAmount();
    this.multiple.set(!!this.paymentPositions().filter(p => p?.id === v)[0]?.isMultiple || false);
    this.count = 1;
  }

  public paymentCategoryChange(v: string): void {
    this.paymentPositions.set(this.paymentCategories()?.filter(j => j?.id === v)[0]?.fees || []);
    this.controls['paymentPosition'].setValue('');
  }

  public entityTypeChange(v: '1' | '2' | ''): void {
    if (v === '1') {
      this.clientFirstName.enable();
      this.clientLastName.enable();

      this.clientName.disable();
      this.nip.disable();
    } else if (v === '2') {
      this.clientFirstName.disable();
      this.clientLastName.disable();

      this.clientName.enable();
      this.nip.enable();
    }
  }

  public readApplicantDataFromPortal(v: UserDetails | null): void {
    this.patchValue({
      clientFirstName: v?.firstName || '',
      clientLastName: v?.lastName || '',
      clientEmail: v?.email || '',
      town: v?.city,
      street: v?.street,
      streetNumber: ((v?.houseNumber && v?.apartmentNumber) ? `${v?.houseNumber}/${v?.apartmentNumber}` : v?.houseNumber) || ''
    }, { emitEvent: false });
  }

  public readApplicantDataFromGovPl(v: TokenData | null): void {
    this.patchValue({
      clientFirstName: v?.firstName || '',
      clientLastName: v?.lastName || '',
      clientEmail: v?.email || '',
      town: '',
      street: '',
      streetNumber: ''
    }, { emitEvent: false });
  }

  public toWriteModel(): PaymentBaseAllBaseWritePaymentAllPaymentWrite {
    const selectedPaymentPosition = this.paymentPositions().filter(p => p?.id === this.paymentPosition.value).at(0);
    return {
      returnUrl: `${environment.ssl ? 'https' : 'http'}://${window.location.host}/platnosci-online`,
      street: this.value.street,
      clientName: this.value?.clientName,
      clientFirstName: this.value?.clientFirstName,
      clientLastName: this.value?.clientLastName,
      town: this.value?.town,
      clientEmail: this.value?.clientEmail,
      streetNumber: this.value?.streetNumber,
      flatNumber: undefined,
      clientDescription: this.value?.clientDescription,
      // jeśli w wybranej paymentPosition jest ustawiona kwota, to wysyłamy positions, jeśli nie to wysyłamy totalAmount, oraz descriptionOverride
      descriptionOverride: selectedPaymentPosition?.price ? undefined : selectedPaymentPosition?.name,
      positions: selectedPaymentPosition?.price ? [{
        fee: this.paymentPosition.value,
        quantity: this.count
      }] : undefined,
      totalAmount: selectedPaymentPosition?.price ? undefined : toNumber(this.totalAmount.value)
    };
  }
}
